import React, { Fragment, useState, useRef, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import { KPI } from "../../components/KPI.js";
import { ActiveConstraintsBar } from "../../components/ui-components/ActiveConstraintsBar.jsx";
import { useQuery, useMutation } from "@tanstack/react-query";
import _ from "underscore";
import ForecastView from "./ForecastView.jsx";
import { useKeycloak } from "@react-keycloak/web";
import { Filter } from "../../components/form-components/Filter";
import HeaderButton from "../../components/ui-components/HeaderButton.js";
import { useFilterStoreWithDayjsDates } from "../../store/filterStore.js";
import dayjs from "dayjs";
import { toast } from '../../components/ui-components/Toast';
import { useForecastSelection } from "../../hooks/useForecastSelection.js";

const API_URL = process.env.REACT_APP_API_URL;
const fillColors = [
	"hsl(var(--chart-1))",
	"hsl(var(--chart-2))",
	"hsl(var(--chart-3))",
	"hsl(var(--chart-teal))",
	"hsl(var(--chart-pred))",
	"hsl(var(--chart-1))",
	"hsl(var(--chart-2))",
	"hsl(var(--chart-3))",
	"hsl(var(--chart-teal))",
	"hsl(var(--chart-pred))",
	"hsl(var(--chart-1))",
];
const filterOptions = {
	keys: [
		{ name: "label", weight: 0.7 },
	],
	valueKey: "key",
	maxPatternLength: 8,
	includeScore: true,
	maxResults: 25,
	threshold: 0.3,
};

const forecastingMethods = [
	{ label: "ARIMA", value: "arima" },
	{ label: "AutoARIMA", value: "autoarima" },
	{ label: "Holt's", value: "holts" },
	{ label: "Prophet", value: "prophet" },
	{ label: "Tide Model", value: "tidemodel" },
	{ label: "SES", value: "ses" }
];

const Forecast = () => {
	// Use Zustand store for filter state with moment dates
	const { 


		analyzerGrouping, 
		analyzerDateGrouping, 
		setAnalyzerDateGrouping,
		filters,
		setFilters
	} = useFilterStoreWithDayjsDates();
	const [selectedForecastMethod, setSelectedForecastMethod] = useState({
		label: "ARIMA",
		value: "arima"
	});
	const [selectedForecastType, setSelectedForecastType] = useState({
		label: "",
		value: ""
	});

	const [totalCosts, setTotalCosts] = useState(0);
	const [cloudCost, setCloudCost] = useState([]);

	// filters now come from Zustand store

	const { keycloak, initialized } = useKeycloak();

	// State to trigger forecastDateRange recalculation when time passes
	// Updates at the start of each month when the date range changes
	const [currentTime, setCurrentTime] = useState(() => dayjs());


	const filtersRef = useRef();
	filtersRef.current = filters;

	// Use module-scope filterOptions (defined at line 35) instead of recreating it
	// The module-scope constant is already defined and stable


	function calcTotalCost(grouped_totals) {
		let currentPeriod = 0;
		let previousPeriod = 0;

		if (grouped_totals !== null && grouped_totals.length > 0) {
			grouped_totals.forEach((group) => {
				currentPeriod += group.current_period;
				previousPeriod += group.previous_period;
			});
		}

		setTotalCosts({
			currentValue: currentPeriod,
			previousValue: previousPeriod,
		});
	}

	function sum(numbers) {
		return _.reduce(
			numbers,
			function (result, current) {
				return result + parseFloat(current);
			},
			0
		);
	}

	function groupCostsByDay(data) {
		const result = _.chain(data)
			.groupBy("time")
			.map(function (value, key) {
				return {
					date: key,
					cost: sum(_.pluck(value, "measure")),
				};
			})
			.value();

		const costs = [];

		result.forEach((item) => {
			costs.push(item.cost);
		});

		return costs;
	}


	// Calculate forecast date range: start of previous month to end of next month
	// Recalculates when currentTime updates (at the start of each month)
	const forecastDateRange = useMemo(() => {
		const now = currentTime;
		const startOfPreviousMonth = now.subtract(1, 'month').startOf('month');
		const endOfNextMonth = now.add(1, 'month').endOf('month');
		return {
			startDate: startOfPreviousMonth,
			endDate: endOfNextMonth,
			formatted: `${startOfPreviousMonth.format("MMM D, YYYY")} - ${endOfNextMonth.format("MMM D, YYYY")}`
		};
	}, [currentTime]);

	// Set up timeout to update currentTime at the start of the next month
	// This ensures forecastDateRange stays current as time passes
	useEffect(() => {
		const now = dayjs();
		const startOfNextMonth = now.add(1, 'month').startOf('month');
		const msUntilNextMonth = startOfNextMonth.diff(now);

		// Set timeout to fire at the start of the next month
		const timeoutId = setTimeout(() => {
			setCurrentTime(dayjs());
		}, msUntilNextMonth);

		// Cleanup timeout on unmount
		return () => {
			clearTimeout(timeoutId);
		};
	}, [currentTime]); // Re-run when currentTime changes to set up next timeout

	function buildAnalyzerRequest() {
		let reqFilters = [];
		let reqMeasures = [];
		let reqGrouping = [];

		

		filtersRef.current.forEach((filter) => {
			let field = filter.category;

			// make sure that the organisation level filter is translated to the organisation_name field in order to prevent double counting
			// however when selecting two orgnisations where one is a parent of the other, the parent should be selected, this should be done in the backend
			if (field.slice(0, 14) === "Organisation l") {
				field = "organisation_name";
			}
			reqFilters.push({
				field: field,
				operator: "=",
				values: filter.selectedValues,
			});
		});

		reqMeasures.push({
			name: "measure",
			formula: "sum(field.direct_cost)",
		});

		if (analyzerGrouping.value.slice(0, 14) === "Organisation l") {
			const level = analyzerGrouping.value.split(" ")[1].substring(1);
			reqFilters.push({ field: "level", operator: "=", values: [level] });
		}

		reqGrouping.push(analyzerGrouping.value);

		// Use forecast date range instead of selected period
		return {
			measures: reqMeasures,
			filters: reqFilters,
			grouping: reqGrouping,
			start_date: forecastDateRange.startDate.format("YYYY-MM-DD"),
			end_date: forecastDateRange.endDate.format("YYYY-MM-DD"),
			group_by_date: true,
			add_grouped_results: true,
			date_granularity: analyzerDateGrouping.label,
		};
	}

	


	    /**
     * Fetches forecast resources from the API filtered by forecast type
     * 
     * @param {string} token - JWT token for authentication
     * @param {string} forecastType - The forecast type to filter by
     * @returns {Promise<Array>} Promise that resolves to array of forecast resource objects
     */
	const fetchForecastedResources = async (token, forecastType) => {
		if (!forecastType) {
			return [];
		}

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + token);

		const url = API_URL + "/get-forecast-resources?forecastType=" + encodeURIComponent(forecastType);
		const response = await fetch(url, {
			method: "GET",
			headers: headers,
		});
		
		if (!response.ok) {
			throw new Error("Failed to fetch forecast resources");
		}
		
		const json = await response.json();
		return prepareForecastedProducts(json.Products);
	};

	// React Query hook for fetching forecast resources - depends on forecast type
	const { data: forecastedProducts = [] } = useQuery({
		queryKey: ['forecastedProducts', keycloak.token, selectedForecastType.value],
		queryFn: () => fetchForecastedResources(keycloak.token, selectedForecastType.value),
		enabled: !!keycloak.token && initialized && !!selectedForecastType.value,
		staleTime: 5 * 60 * 1000, // 5 minutes
		cacheTime: 10 * 60 * 1000, // 10 minutes
	});

	// Filter products to show only unique products in dropdown (group by product_id)
	// When multiple entries exist for the same product_id, keep the first one encountered
	// Ensure "total" row is always included if present in the API response
	const uniqueProducts = useMemo(() => {
		const productMap = new Map();
		let totalProduct = null;
		const totalMatches = [];
		
		// Check for "total" entries first to validate uniqueness
		for (const product of forecastedProducts) {
			// Check if this is the total row (value maps to product_id, label maps to product_name from API)
			const isTotal = product.value === "total" || product.label === "total" || 
			               product.value?.toLowerCase() === "total" || product.label?.toLowerCase() === "total";
			if (isTotal) {
				totalMatches.push(product);
			}
		}
		
		// Validate that only one "total" entry exists
		if (totalMatches.length > 1) {
			console.warn(`Multiple "total" entries found in forecastedProducts (${totalMatches.length}). Using the first match.`, totalMatches);
		}
		
		// Use the first match if any exist
		if (totalMatches.length > 0) {
			totalProduct = totalMatches[0];
		}
		
		// Process other products
		for (const product of forecastedProducts) {
			// Skip if this is a total product (already handled above)
			const isTotal = product.value === "total" || product.label === "total" || 
			               product.value?.toLowerCase() === "total" || product.label?.toLowerCase() === "total";
			if (!isTotal && !productMap.has(product.value)) {
				productMap.set(product.value, product);
			}
		}
		
		const products = Array.from(productMap.values());
		
		// Add total row at the beginning if it exists
		if (totalProduct) {
			products.unshift(totalProduct);
		}
		
		return products;
	}, [forecastedProducts]);

	// Use custom hook for forecast selection state management
	// This hook handles auto-selection logic without circular dependencies
	const {
		selectedForecastProduct,
		selectedForecastCategory,
		selectedForecastProductID,
		selectedForecastSeries,
		setProduct: setForecastProduct,
		setSeries: setForecastSeries,
	} = useForecastSelection(uniqueProducts, forecastedProducts, selectedForecastType.value);

	// Get available series for selected product
	// This is calculated after the hook so it can use selectedForecastProductID from the hook
	const availableSeries = useMemo(() => {
		if (!selectedForecastProductID || selectedForecastType.value === "cost") {
			return [];
		}
		const productSeries = forecastedProducts
			.filter(p => p.value === selectedForecastProductID && p.series)
			.map(p => p.series)
			.filter((value, index, self) => self.indexOf(value) === index); // unique values
		return productSeries.map(s => ({ label: s, value: s }));
	}, [forecastedProducts, selectedForecastProductID, selectedForecastType.value]);

	// Auto-select first series when product changes or series become available
	// Uses refs to track previous values and avoid circular dependencies
	const prevProductIDForSeriesRef = useRef(selectedForecastProductID);
	const prevAvailableSeriesLengthRef = useRef(availableSeries.length);
	
	// Memoize series validity check to avoid stale closure
	// This memo updates when selectedForecastSeries or availableSeries change
	const currentSeriesValid = useMemo(() => {
		if (!selectedForecastSeries || !availableSeries || availableSeries.length === 0) {
			return false;
		}
		return availableSeries.some(s => s.value === selectedForecastSeries);
	}, [selectedForecastSeries, availableSeries]);
	
	useEffect(() => {
		const productIDChanged = prevProductIDForSeriesRef.current !== selectedForecastProductID;
		const seriesBecameAvailable = prevAvailableSeriesLengthRef.current === 0 && availableSeries.length > 0;
		
		// Update refs
		prevProductIDForSeriesRef.current = selectedForecastProductID;
		prevAvailableSeriesLengthRef.current = availableSeries.length;
		
		// Only auto-select if product changed or series became available
		if (productIDChanged || seriesBecameAvailable) {
			if (selectedForecastType.value !== "cost" && selectedForecastProductID && availableSeries.length > 0) {
				// Use memoized validity check instead of reading selectedForecastSeries directly
				// This ensures we always have the current value
				if (!currentSeriesValid) {
					setForecastSeries(availableSeries[0].value);
				}
			} else if (selectedForecastType.value === "cost") {
				setForecastSeries("");
			}
		}
	}, [availableSeries, selectedForecastType.value, selectedForecastProductID, setForecastSeries, currentSeriesValid]);

	    /**
     * Transforms organization data into a format suitable for dropdown/select components
     * 
     * This function maps each organization element to an object with standardized fields:
     * - category: The organization level description
     * - category_description: The organization level description (duplicate for consistency)
     * - label: The organization name to display in UI
     * - value: The organization name to use as the select value
     * 
     * @param {Array} data - Array of organization objects containing level_description and name
     * @returns {Array} Array of transformed organization objects with standardized fields
     */
		const prepareForecastedProducts = (data) => {
			return data.map((element) => {
				return {
					category: element.category,
					category_description: element.category,
					label: element.product_name,
					value: element.product_id,
					series: element.series || ""
				}
			});
		}

	/**
	 * Fetches forecast types from the API
	 * 
	 * @param {string} token - JWT token for authentication
	 * @returns {Promise<Array>} Promise that resolves to array of forecast type strings
	 */
	const fetchForecastTypes = async (token) => {
		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + token);

		const response = await fetch(API_URL + "/get-forecast-types", {
			method: "GET",
			headers: headers,
		});
		
		if (!response.ok) {
			throw new Error("Failed to fetch forecast types");
		}
		
		const json = await response.json();
		return json.Types || [];
	};

	// React Query hook for fetching forecast types
	const { data: forecastTypesRaw = [] } = useQuery({
		queryKey: ['forecastTypes', keycloak.token],
		queryFn: () => fetchForecastTypes(keycloak.token),
		enabled: !!keycloak.token && initialized,
		staleTime: 5 * 60 * 1000, // 5 minutes
		cacheTime: 10 * 60 * 1000, // 10 minutes
	});

	// Set default forecast type when types are loaded (including from cache)
	// This ensures a default is set even when switching tabs
	useEffect(() => {
		if (forecastTypesRaw && forecastTypesRaw.length > 0 && !selectedForecastType.value) {
			setSelectedForecastType({
				label: forecastTypesRaw[0],
				value: forecastTypesRaw[0]
			});
		}
	}, [forecastTypesRaw, selectedForecastType.value]);

	// Transform forecast types array of strings into format needed for Filter component
	const forecastTypes = forecastTypesRaw.map((type) => ({
		label: type,
		value: type
	}));

	const retrieveCloudCostForecast = async (filtersRef, selectedForecastProductID, forecastType) => {
		if (
			filtersRef === undefined ||
			selectedForecastProductID === "" ||
			!forecastType
		)
			return;

		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + keycloak.token);

		let method = "POST";

		const requestBody = buildAnalyzerRequest();

		let requestOptions = {
			body: JSON.stringify(requestBody),
			method: method,
			headers: headers,
		};

		let url = API_URL + "/get-forecast?category=" + selectedForecastProduct.category + "&productID=" + selectedForecastProduct.value + "&forecastType=" + encodeURIComponent(forecastType);
		if (selectedForecastSeries && forecastType !== "cost") {
			url += "&series=" + encodeURIComponent(selectedForecastSeries);
		}
		const response = await fetch(url, requestOptions);

		if (!response.ok) {
			throw new Error("Failed to fetch data");
		}

		return response.json();
	};

	useQuery(
		["cloudCostForecast", filtersRef, selectedForecastCategory, selectedForecastProductID, selectedForecastType.value, selectedForecastSeries],
		() =>
			retrieveCloudCostForecast(
				filtersRef,
				selectedForecastProductID,
				selectedForecastType.value
			),
		{
			onSuccess: (data) => {
				if (data.CloudCost && data.CloudCost.details) {
					data.CloudCost.details.forEach((d) => {
						d.time = d.groupings[0].value;
						d.grouping = d.groupings[1].value;
						d.measure = d.measures[0].value;
						d.forecast = d.measures[1].value;
					});

					setCloudCost(data.CloudCost);
					calcTotalCost(data.CloudCost.grouped_totals);

				} else {
					setCloudCost([]);
					calcTotalCost([]);
				}
			},
			enabled:
				filtersRef.current !== undefined &&
				selectedForecastProductID !== "" &&
				!!selectedForecastType.value &&
				(selectedForecastType.value === "cost" || selectedForecastSeries !== ""),
			staleTime: 5000, // Optional: Prevent frequent refetching
		}
	);

	// Mutation for running forecast algorithms
	const runForecastMutation = useMutation({
		mutationFn: async (algorithm) => {
			const headers = new Headers();
			headers.append("Content-Type", "application/json");
			headers.append("Authorization", "Bearer " + keycloak.token);

			const requestOptions = {
				method: "POST",
				headers: headers,
			};

			const response = await fetch(API_URL + `/run-forecast-and-insights-algorithms?algorithm=${algorithm}`, requestOptions);
			
			if (!response.ok) {
				throw new Error("Failed to run forecast algorithm");
			}

			return response.json();
		},
		onSuccess: (data) => {
			toast.success("Forecast algorithm completed successfully.");
		},
		onError: (error) => {
			console.error("Error running forecast algorithm:", error);
			// You can add error notification here if needed
		}
	});

	const handleRunForecast = () => {
		runForecastMutation.mutate(selectedForecastMethod.value);
	};

	// Calculate forecast title with series if available
	const forecastTitle = useMemo(() => {
		if (!selectedForecastProduct.label) {
			return "Trend by " + analyzerDateGrouping.label;
		}
		
		const resourceName = selectedForecastProduct.label;
		const forecastType = selectedForecastType.value;
		const series = String(selectedForecastSeries || "").trim();
		
		// For utilization forecasts, include series if available
		if (forecastType === "utilization" && series !== "") {
			return `Forecast for ${resourceName} (${series})`;
		}
		
		// For cost forecasts or when no series is selected, show without series
		return `Forecast for ${resourceName}`;
	}, [selectedForecastProduct.label, selectedForecastSeries, selectedForecastType.value, analyzerDateGrouping.label]);

	return (
		<Fragment>
			<AdministrationHeader 
				title="Economic Control"
				className="text-text-3"
				icon={icon({name: "display-chart-up", style: "light"})}
				filters={Array.isArray(filters) ? filters : []}
				setFilters={setFilters}
			/>

			<ActiveConstraintsBar token={keycloak.token} filters={Array.isArray(filters) ? filters : []} setFilters={setFilters} />

			{/* Content */}
			<div className="">
				<div className="text-left flex flex-col ml-10 border-solid border-border">
					<div className="flex flex-row">
						<div>
							<KPI
								title="Total Costs"
								currentValue={totalCosts.currentValue}
								previousValue={totalCosts.previousValue}
								trendData={groupCostsByDay(cloudCost.details)}
								format="currency"
								showChange={true}
								style="small"
							/>

						</div>
						<div className="mr-8 ml-auto">
							<div className="text-left flex text-text-2 gap-4">
								{/* Forecast Type Dropdown */}
								<div className="min-w-[200px] pb-2 cursor-pointer">
									<div className="flex flex-row pl-4 pt-1 border border-solid border-border rounded-full h-[34px] bg-card">
										<div className="flex flex-row min-w-full">
											<div className="flex-1">
												<Filter
													options={forecastTypes}
													fuzzyOptions={filterOptions}
													autoCorrect="off"
													spellCheck="off"
													width={200}
													dropdownWidth={200}
													hideButtons={true}
													title="Type"
													selectedValues={selectedForecastType.label}
													getOptionLabel={(option) => option.label}
													getOptionValue={(option) => option.value}
													multi={false}
													handleChange={(e) => {
														setSelectedForecastType({value: e.value, label: e.label});
													}}
													isSearchable={false}
													textColor={"hsl(var(--text-2))"}
													showCategory={false}
													backgroundColor="white"
												/>
											</div>

											<div className="flex-none right-0 w-8 mr-6">
												<FontAwesomeIcon
													icon={icon({
														name: "angle-down",
														family: "sharp",
														style: "regular",
													})}
													className="ml-2 pt-1 text-text-2"
												/>
											</div>
										</div>
									</div>
								</div>

								{/* Product Selection Dropdown */}
								<div className="min-w-[340px] pb-2 cursor-pointer">
									<div className="flex flex-row pl-4 pt-1 border border-solid border-border rounded-full h-[34px] bg-card">
										<div className="flex flex-row min-w-full">
											<div className="flex-1">
												<Filter
													options={uniqueProducts}
													fuzzyOptions={filterOptions}
													autoCorrect="off"
													spellCheck="off"
													width={340}
													dropdownWidth={340}
													hideButtons={true}
													title={selectedForecastCategory ? selectedForecastCategory.charAt(0).toUpperCase() + selectedForecastCategory.slice(1) : (selectedForecastType.value ? "Select Product" : "Select Type First")}
													selectedValues={selectedForecastProduct.label || (selectedForecastType.value && uniqueProducts.length === 0 ? "Loading..." : "")}
													getOptionLabel={(option) => option.label}
													getOptionValue={(option) => option.value}
													multi={false}
													handleChange={(e) => {
														// Find the product in the original list to get the correct category
														const originalProduct = forecastedProducts.find(p => p.value === e.value);
														const productCategory = originalProduct?.category || e.category || "";
														
														// Use hook's setProduct function to update all related state
														const series = (selectedForecastType.value !== "cost" && e.series) 
															? e.series 
															: "";
														setForecastProduct(
															{ value: e.value, label: e.label, category: productCategory },
															productCategory,
															e.value,
															series
														);
													}}
													isSearchable={false}
													textColor={"hsl(var(--text-2))"}
													showCategory={true}
													backgroundColor="white"
												/>
											</div>

											<div className="flex-none right-0 w-8 mr-6">
												<FontAwesomeIcon
													icon={icon({
														name: "angle-down",
														family: "sharp",
														style: "regular",
													})}
													className="ml-2 pt-1 text-text-2"
												/>
											</div>
										</div>
									</div>
								</div>

								{/* Series Selection Dropdown - Only shown for non-cost forecast types */}
								{selectedForecastType.value !== "cost" && (
									<div className="min-w-[200px] pb-2 cursor-pointer">
										<div className="flex flex-row pl-4 pt-1 border border-solid border-border rounded-full h-[34px] bg-card">
											<div className="flex flex-row min-w-full">
												<div className="flex-1">
													<Filter
														options={availableSeries}
														fuzzyOptions={filterOptions}
														autoCorrect="off"
														spellCheck="off"
														width={200}
														dropdownWidth={200}
														hideButtons={true}
														title="Series"
														selectedValues={selectedForecastSeries || (availableSeries.length === 0 ? "No series" : "")}
														getOptionLabel={(option) => option.label}
														getOptionValue={(option) => option.value}
														multi={false}
														handleChange={(e) => {
															setForecastSeries(e.value);
														}}
														isSearchable={false}
														textColor={"hsl(var(--text-2))"}
														showCategory={false}
														backgroundColor="white"
														disabled={availableSeries.length === 0}
													/>
												</div>

												<div className="flex-none right-0 w-8 mr-6">
													<FontAwesomeIcon
														icon={icon({
															name: "angle-down",
															family: "sharp",
															style: "regular",
														})}
														className="ml-2 pt-1 text-text-2"
													/>
												</div>
											</div>
										</div>
									</div>
								)}

								{/* Forecasting Method Dropdown */}
								<div className="min-w-[200px] pb-2 cursor-pointer">
									<div className="flex flex-row pl-4 pt-1 border border-solid border-border rounded-full h-[34px] bg-card">
										<div className="flex flex-row min-w-full">
											<div className="flex-1">
												<Filter
													options={forecastingMethods}
													fuzzyOptions={filterOptions}
													autoCorrect="off"
													spellCheck="off"
													width={200}
													dropdownWidth={200}
													hideButtons={true}
													title="Method"
													selectedValues={selectedForecastMethod.label}
													getOptionLabel={(option) => option.label}
													getOptionValue={(option) => option.value}
													multi={false}
													handleChange={(e) => {
														setSelectedForecastMethod({value: e.value, label: e.label});
													}}
													isSearchable={false}
													textColor={"hsl(var(--text-2))"}
													showCategory={false}
													backgroundColor="white"
												/>
											</div>

											<div className="flex-none right-0 w-8 mr-6">
												<FontAwesomeIcon
													icon={icon({
														name: "angle-down",
														family: "sharp",
														style: "regular",
													})}
													className="ml-2 pt-1 text-text-2"
												/>
											</div>
										</div>
									</div>
								</div>

								{/* Run Forecast Button */}
								<div className="pb-2">
									<HeaderButton
										title="Run Forecast"
										type="secondary"
										icon={icon({name: "play", style: "regular"})}
										onClick={handleRunForecast}
										isLoading={runForecastMutation.isPending}
									/>
								</div>
							</div>

							{/* <Dropdown
								valuePrefix="Grouping : "
								options={groupingValues}
								dropdownWidth={340}
								selectedValue={analyzerGrouping}
								setSelectedValue={setAnalyzerGrouping}
								alignRight={true}
							/> */}
						</div>
					</div>


					<ForecastView 
						data={cloudCost.details}
						groupedData={cloudCost.grouped_totals}
						dateFormat={analyzerDateGrouping.value}
						dateGrouping={analyzerDateGrouping}
						setDateGrouping={setAnalyzerDateGrouping}
						title={forecastTitle}
						subtitle={forecastDateRange.formatted}
						valueName="Total Costs"
						showButtons={true}
						grouping={analyzerGrouping.label}
						fillColors={fillColors}
						selectedResource={selectedForecastProduct.label}
						forecastType={selectedForecastType.value}
					/>
				</div>
			</div>
		</Fragment>
	);
};

export default Forecast;
